import SelectField from "../../../../common/SelectField";
import TextField from "../../../../common/TextField";
import { ensureHttpsPrefix } from "../../../../../../utils/planManager/planConfigurationUtils.js";
import "./VendorDetails.scss";

/**
 * Vendor Details step. Presentational — renders the telemedicine radio group,
 * PBM select, and PBM/formulary URL inputs, emitting each change upward via
 * `onFieldChange`. Owns no state.
 *
 * @param {object} props
 * @param {Object} props.values - The Vendor Details slice of the wizard form state.
 * @param {Object} props.options - Settings-derived dropdown and radio options.
 * @param {Record<string, string>} [props.errors] - Field-level validation errors.
 * @param {(field: string, value: *) => void} props.onFieldChange - Emits a single field change.
 * @returns {import('react').JSX.Element}
 */
const VendorDetails = ({ values, options, errors = {}, onFieldChange }) => {
    const telemedicineOptions = options?.telemedicineOptions ?? [];
    const pbmOptions = (options?.pbmNames ?? []).map((pbmName) => ({
        value: pbmName,
        label: pbmName,
    }));
    const isCustomTelemedicine = values.telemedicine === "Other";
    const telemedicineError = errors.telemedicine;

    return (
        <div className="plan-setup-step plan-setup-vendor-details">
            <fieldset
                className={[
                    "plan-setup-vendor-details__group",
                    telemedicineError
                        ? "plan-setup-vendor-details__group--error"
                        : null,
                ]
                    .filter(Boolean)
                    .join(" ")}
                aria-required="true"
                aria-invalid={telemedicineError ? "true" : undefined}
            >
                <legend>
                    Telemedicine
                    <span
                        className="plan-setup-vendor-details__required"
                        aria-hidden="true"
                    >
                        {" "}
                        *
                    </span>
                </legend>
                {telemedicineOptions.map((option) => (
                    <label
                        key={option}
                        className="plan-setup-vendor-details__radio"
                    >
                        <input
                            type="radio"
                            name="telemedicine"
                            value={option}
                            required
                            checked={values.telemedicine === option}
                            onChange={(event) =>
                                onFieldChange(
                                    "telemedicine",
                                    event.target.value,
                                )
                            }
                        />
                        <span>{option}</span>
                    </label>
                ))}
                {telemedicineError ? (
                    <p className="plan-setup-vendor-details__error" role="alert">
                        {telemedicineError}
                    </p>
                ) : null}
            </fieldset>

            {isCustomTelemedicine ? (
                <TextField
                    id="telemedicine-custom"
                    label="Telemedicine Provider"
                    value={values.telemedicineCustom}
                    error={errors.telemedicineCustom}
                    onChange={(value) =>
                        onFieldChange("telemedicineCustom", value)
                    }
                />
            ) : null}

            <SelectField
                id="pbm-name"
                label="PBM"
                value={values.pbmName}
                error={errors.pbmName}
                onChange={(value) => onFieldChange("pbmName", value)}
                placeholderOption="Select PBM Name"
                options={pbmOptions}
            />

            <TextField
                id="pbm-site-url"
                label="PBM Site URL (if applicable)"
                value={values.pbmSiteURL}
                onChange={(value) => onFieldChange("pbmSiteURL", value)}
                onBlur={() =>
                    onFieldChange(
                        "pbmSiteURL",
                        ensureHttpsPrefix(values.pbmSiteURL),
                    )
                }
            />

            <TextField
                id="formulary-url"
                label="Formulary URL (if applicable)"
                value={values.formularyUrl}
                onChange={(value) => onFieldChange("formularyUrl", value)}
                onBlur={() =>
                    onFieldChange(
                        "formularyUrl",
                        ensureHttpsPrefix(values.formularyUrl),
                    )
                }
            />
        </div>
    );
};

export default VendorDetails;
